import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import Papa from 'papaparse';
import config from '../../config';

const BulkImportDialog = ({ open, onClose, onImportComplete, token }) => {
  const [step, setStep] = useState(1); // 1: Select Type, 2: Upload File, 3: Processing, 4: Results
  const [organizationType, setOrganizationType] = useState('school');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);

  const handleClose = () => {
    setStep(1);
    setOrganizationType('school');
    setFile(null);
    setUploading(false);
    setProgress(0);
    setResults(null);
    onClose();
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      // Check file type
      const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
      if (fileExtension === 'csv' || fileExtension === 'xlsx' || fileExtension === 'xls') {
        setFile(selectedFile);
      } else {
        alert('Please select a CSV or Excel file');
      }
    }
  };

  const parseBusinessCSV = (data) => {
    // Map CSV columns for the advertisers/businesses CSV format:
    // [0] Customer ID, [1] Organisation Name, [2] ABN, [3] Website
    // [4] Street Address, [5] Suburb, [6] State, [7] Postcode
    // [14]+[15] 1st Contact (First+Last Name) → primaryName, [17] email, [18] phone
    // [19]+[20] 2nd Contact → businessManagerName, [22] email, [23] phone
    // [24]+[25] 3rd Contact → principalName, [27] email, [28] phone

    return data.map((row, index) => {
      // Skip header row
      if (index === 0) return null;

      // Skip empty rows
      if (!row[1] || row[1].toString().trim() === '') return null;

      const contact1Name = [row[14], row[15]].filter(v => v && v.toString().trim()).join(' ').trim();
      const contact2Name = [row[19], row[20]].filter(v => v && v.toString().trim()).join(' ').trim();
      const contact3Name = [row[24], row[25]].filter(v => v && v.toString().trim()).join(' ').trim();

      return {
        customer_id: row[0] ? row[0].toString() : '',
        name: row[1] ? row[1].toString().trim() : '',
        website: row[3] ? row[3].toString().trim() : '',
        address: row[4] ? row[4].toString().trim() : '',
        suburb: row[5] ? row[5].toString().trim() : '',
        state: row[6] ? row[6].toString().trim() : '',
        postcode: row[7] ? row[7].toString().trim() : '',
        primary_name: contact1Name,
        primary_email: row[17] ? row[17].toString().trim() : '',
        primary_phone: row[18] ? row[18].toString().trim() : '',
        business_manager_name: contact2Name,
        business_manager_email: row[22] ? row[22].toString().trim() : '',
        business_manager_phone: row[23] ? row[23].toString().trim() : '',
        principal_name: contact3Name,
        principal_email: row[27] ? row[27].toString().trim() : '',
        principal_phone: row[28] ? row[28].toString().trim() : '',
        is_school: false,
        is_business: true,
        is_active: true
      };
    }).filter(org => org !== null);
  };

  const parseSchoolCSV = (data) => {
    // Map CSV columns to organization fields based on the specification
    // A = ID (index 0), B = Name (index 1), E = Address (index 4)
    // O = Business Manager Name (index 14), R = Business Manager Email (index 17), S = Business Manager Phone (index 18)
    // T & U = Principal First & Last Name (index 19, 20), W = Principal Email (index 22), X = Principal Phone (index 23)
    // Y = Primary Contact Name (index 24), AB = Primary Contact Email (index 27), AC = Primary Contact Phone (index 28)
    
    return data.map((row, index) => {
      // Skip header row
      if (index === 0) return null;
      
      // Skip empty rows
      if (!row[1] || row[1].trim() === '') return null;

      return {
        customer_id: row[0] || '',
        name: row[1] || '',
        address: row[4] || '',
        suburb: row[5] || '',
        state: row[6] || '',
        postcode: row[7] || '',
        business_manager_name: row[14] || '',
        business_manager_email: row[17] || '',
        business_manager_phone: row[18] || '',
        principal_name: row[19] && row[20] ? `${row[19]} ${row[20]}`.trim() : (row[19] || row[20] || ''),
        principal_email: row[22] || '',
        principal_phone: row[23] || '',
        primary_name: row[24] || '',
        primary_email: row[27] || '',
        primary_phone: row[28] || '',
        is_school: true,
        is_business: false,
        is_active: true
      };
    }).filter(org => org !== null);
  };

  const handleUpload = async () => {
    if (!file) {
      alert('Please select a file');
      return;
    }

    setStep(3);
    setUploading(true);
    setProgress(0);

    // Parse CSV file
    Papa.parse(file, {
      complete: async (result) => {
        try {
          let organizations;
          
          if (organizationType === 'school') {
            organizations = parseSchoolCSV(result.data);
          } else {
            organizations = parseBusinessCSV(result.data);
          }

          // Split into batches of 500 to avoid timeouts on large imports
          const BATCH_SIZE = 500;
          const batches = [];
          for (let i = 0; i < organizations.length; i += BATCH_SIZE) {
            batches.push(organizations.slice(i, i + BATCH_SIZE));
          }

          let totalCreated = 0;
          let totalUpdated = 0;
          let totalSkipped = 0;
          let allErrors = [];

          for (let b = 0; b < batches.length; b++) {
            const batchProgress = Math.round(((b) / batches.length) * 90);
            setProgress(batchProgress);

            const response = await fetch(`${config.apiUrl}/api/organizations/bulk-import`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                organizations: batches[b],
                organizationType,
                batchInfo: { batch: b + 1, totalBatches: batches.length }
              })
            });

            const data = await response.json();

            if (!data.success) {
              // One batch failed — report but continue with remaining batches
              allErrors.push({ organization: `Batch ${b + 1}`, message: data.message || 'Batch failed' });
              totalSkipped += batches[b].length;
            } else {
              totalCreated += data.created || 0;
              totalUpdated += data.updated || 0;
              totalSkipped += data.skipped || 0;
              if (data.errors) allErrors = allErrors.concat(data.errors);
            }
          }

          setProgress(100);
          setUploading(false);
          setStep(4);
          setResults({
            success: true,
            created: totalCreated,
            updated: totalUpdated,
            skipped: totalSkipped,
            errors: allErrors.length > 0 ? allErrors : undefined,
            message: `Successfully imported ${totalCreated + totalUpdated} organisations across ${batches.length} batch${batches.length > 1 ? 'es' : ''}`
          });

          setTimeout(() => {
            onImportComplete();
          }, 2000);

        } catch (error) {
          console.error('Error uploading file:', error);
          setUploading(false);
          setResults({
            success: false,
            message: 'Error processing file: ' + error.message
          });
          setStep(4);
        }
      },
      error: (error) => {
        console.error('Error parsing CSV:', error);
        setUploading(false);
        setResults({
          success: false,
          message: 'Error parsing CSV file: ' + error.message
        });
        setStep(4);
      }
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Bulk Import Organisations
      </DialogTitle>
      
      <DialogContent>
        {/* Step 1: Select Organisation Type */}
        {step === 1 && (
          <Box sx={{ py: 2 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Select Organisation Type</FormLabel>
              <RadioGroup
                value={organizationType}
                onChange={(e) => setOrganizationType(e.target.value)}
                sx={{ mt: 2 }}
              >
                <FormControlLabel 
                  value="school" 
                  control={<Radio />} 
                  label="Schools" 
                />
                <FormControlLabel 
                  value="business" 
                  control={<Radio />} 
                  label="Businesses" 
                />
              </RadioGroup>
            </FormControl>
            
            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="body2">
                {organizationType === 'school' ? (
                  <>
                    <strong>Expected CSV Format for Schools:</strong><br />
                    Column A: Customer ID (will be used as Organisation ID)<br />
                    Column B: Organisation Name<br />
                    Column E: Street Address<br />
                    Column O: Business Manager Name<br />
                    Column R: Business Manager Email<br />
                    Column S: Business Manager Phone<br />
                    Columns T & U: Principal First & Last Name<br />
                    Column W: Principal Email<br />
                    Column X: Principal Phone<br />
                    Column Y: Primary Contact Name<br />
                    Column AB: Primary Contact Email<br />
                    Column AC: Primary Contact Phone
                  </>
                ) : (
                  <>
                    <strong>Expected CSV Format for Businesses:</strong><br />
                    Column A: Customer ID (will be used as Organisation ID)<br />
                    Column B: Organisation Name<br />
                    Column D: Website<br />
                    Column E: Street Address, Column F: Suburb, Column G: State, Column H: Postcode<br />
                    Columns O & P: 1st Contact First & Last Name → Primary Contact<br />
                    Column R: 1st Contact Email, Column S: 1st Contact Phone<br />
                    Columns T & U: 2nd Contact → Business Manager Name<br />
                    Column W: 2nd Contact Email, Column X: 2nd Contact Phone<br />
                    Columns Y & Z: 3rd Contact → Principal Name<br />
                    Column AB: 3rd Contact Email, Column AC: 3rd Contact Phone
                  </>
                )}
              </Typography>
            </Alert>
            
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Important:</strong> The Customer ID from Column A will be preserved as the organization's primary ID in the system. 
                After import, the auto-increment will be set to continue from the highest ID + 1.
              </Typography>
            </Alert>
          </Box>
        )}

        {/* Step 2: Upload File */}
        {step === 2 && (
          <Box sx={{ py: 2 }}>
            <Typography variant="body1" gutterBottom>
              Selected Type: <strong>{organizationType === 'school' ? 'Schools' : 'Businesses'}</strong>
            </Typography>
            
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 4, 
                mt: 3, 
                textAlign: 'center',
                border: '2px dashed #ccc',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#f5f5f5'
                }
              }}
              onClick={() => document.getElementById('file-upload').click()}
            >
              <input
                id="file-upload"
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              
              <CloudUploadIcon sx={{ fontSize: 60, color: '#999', mb: 2 }} />
              
              <Typography variant="h6" gutterBottom>
                {file ? file.name : 'Click to select file'}
              </Typography>
              
              <Typography variant="body2" color="text.secondary">
                Supported formats: CSV, Excel (.xlsx, .xls)
              </Typography>
            </Paper>
          </Box>
        )}

        {/* Step 3: Processing */}
        {step === 3 && (
          <Box sx={{ py: 4 }}>
            <Typography variant="h6" gutterBottom align="center">
              Processing Import...
            </Typography>
            
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ mt: 3, mb: 2 }}
            />
            
            <Typography variant="body2" align="center" color="text.secondary">
              {progress < 10 && 'Parsing file...'}
              {progress >= 10 && progress < 90 && `Uploading organisations... (${Math.round(progress)}%)`}
              {progress >= 90 && 'Finalising import...'}
            </Typography>
          </Box>
        )}

        {/* Step 4: Results */}
        {step === 4 && results && (
          <Box sx={{ py: 2 }}>
            {results.success ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <CheckCircleIcon color="success" sx={{ fontSize: 40, mr: 2 }} />
                  <Typography variant="h6">
                    Import Completed Successfully!
                  </Typography>
                </Box>
                
                <Alert severity="success" sx={{ mb: 2 }}>
                  {results.message || `${(results.created || 0) + (results.updated || 0)} organisations imported successfully`}
                  {results.skipped > 0 && ` (${results.skipped} skipped)`}
                </Alert>

                {results.errors && results.errors.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="error" gutterBottom>
                      Errors:
                    </Typography>
                    <List dense>
                      {results.errors.slice(0, 10).map((error, index) => (
                        <ListItem key={index}>
                          <ListItemText 
                            primary={error.message || error}
                            secondary={error.organization || ''}
                          />
                        </ListItem>
                      ))}
                      {results.errors.length > 10 && (
                        <ListItem>
                          <ListItemText 
                            primary={`... and ${results.errors.length - 10} more errors`}
                          />
                        </ListItem>
                      )}
                    </List>
                  </Box>
                )}
              </>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <ErrorIcon color="error" sx={{ fontSize: 40, mr: 2 }} />
                  <Typography variant="h6">
                    Import Failed
                  </Typography>
                </Box>
                
                <Alert severity="error">
                  {results.message || 'An error occurred during import'}
                </Alert>
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {step === 1 && (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button 
              variant="contained" 
              onClick={() => setStep(2)}
            >
              Next
            </Button>
          </>
        )}
        
        {step === 2 && (
          <>
            <Button onClick={() => setStep(1)}>Back</Button>
            <Button onClick={handleClose}>Cancel</Button>
            <Button 
              variant="contained" 
              onClick={handleUpload}
              disabled={!file}
            >
              Upload & Import
            </Button>
          </>
        )}
        
        {step === 3 && (
          <Button disabled>Processing...</Button>
        )}
        
        {step === 4 && (
          <Button variant="contained" onClick={handleClose}>
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkImportDialog;

